/**
 * Attach a supporting document to an RFQ.
 *
 * The endpoint (`RFQDocumentViewSet`) has always accepted these uploads and the
 * API module has always wrapped it — there was simply no control anywhere in
 * the app, so both the wizard and the detail page showed "No documents attached
 * to this RFQ" with nothing to click, and the drawings and specifications an
 * enquiry depends on had to travel by email.
 *
 * Write is customer-only, matching `_CUSTOMER_WRITE` on the viewset: a supplier
 * reads the buyer's documents and returns its own through the certificate and
 * quote surfaces instead.
 */
import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import FileDropZone from '../common/FileDropZone';

interface RFQDocumentUploadProps {
  rfqId: number;
  /** Refetch the RFQ so the new document appears in the list. */
  onUploaded: () => void;
}

/** Mirrors RFQDocument.DOCUMENT_TYPE_CHOICES exactly — an unlisted value is a 400. */
const DOCUMENT_TYPES = [
  { value: 'drawing', label: 'Engineering Drawing' },
  { value: 'technical_spec', label: 'Technical Specification' },
  { value: 'sow', label: 'Statement of Work' },
  { value: 'terms', label: 'Terms & Conditions' },
  // The agreement suppliers must accept on an NDA-required RFQ. Lets a
  // customer attach it to an existing draft; the send step refuses without it.
  { value: 'nda', label: 'Non-Disclosure Agreement' },
  { value: 'other', label: 'Other' },
];

export default function RFQDocumentUpload({ rfqId, onUploaded }: RFQDocumentUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState('drawing');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFile(null);
    setTitle('');
    setDocumentType('drawing');
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Choose a file to attach');
      return;
    }
    const form = new FormData();
    form.append('rfq', String(rfqId));
    form.append('file', file);
    // The server derives file_name/file_size/file_type from the upload itself,
    // so only the fields a person actually chooses are sent.
    form.append('document_type', documentType);
    // Explicit: a boolean missing from multipart reads as False server-side,
    // which used to create every uploaded document inactive.
    form.append('is_active', 'true');
    form.append('title', title.trim() || file.name);

    try {
      setBusy(true);
      await rfqAPI.uploadDocument(form);
      toast.success('Document attached');
      reset();
      onUploaded();
    } catch (error: unknown) {
      console.error('Error uploading RFQ document:', error);
      toast.error('Failed to attach document');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="space-y-1">
          <Label className="text-xs" htmlFor="rfq-doc-file">File</Label>
          {/* Customer review 11 Sep (drag and drop everywhere). */}
          <FileDropZone
            size="compact"
            inputId="rfq-doc-file"
            disabled={busy}
            hint={file ? <>Selected: <span className="font-medium text-foreground">{file.name}</span></> : undefined}
            onFiles={(files) => setFile(files[0])}
            data-testid="rfq-document-drop-zone"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="rfq-doc-title">Title</Label>
            <Input
              id="rfq-doc-title"
              value={title}
              disabled={busy}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={file ? file.name : 'Defaults to the file name'}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="rfq-doc-type">Type</Label>
            <select
              id="rfq-doc-type"
              value={documentType}
              disabled={busy}
              onChange={(e) => setDocumentType(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Every invited supplier can see what you attach here.
          </p>
          <Button onClick={handleUpload} disabled={busy || !file} size="sm" className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Attach document
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
